import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import {
  LicenceAdjustmentResponse,
  LicencePortfolioResponse,
  LicenceResponse,
  LicenceResumeResponse,
  LicenceShowResponse,
} from '../../../Interfaces/api/backoffice/licence.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { Licence } from '../../../Models/backoffice/licence.model';
import { LicenceComponent } from '../../../Models/backoffice/licence-component.model';
import { LicenceAdjustPayload, LicenceFilters, LicenceResumePayload } from '../Interfaces/licence-filters.interface';

@Injectable({
  providedIn: 'root',
})
export class OrganizationLicenceService {
  private readonly _apiService = inject(ApiService);

  portfolioRequest(
    organizationId: string,
    filters: LicenceFilters,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<LicencePortfolioResponse>> {
    return this._apiService.get<LaravelDefaultResponse<LicencePortfolioResponse>>(
      endpoints.backoffice.organizationLicences(organizationId),
      { params: this._params(filters, page, pageSize), skipLoading: true, skipError: true },
    );
  }

  adjust(
    organizationId: string,
    payload: LicenceAdjustPayload,
  ): Observable<LicenceAdjustmentResponse> {
    const request = this._apiService.post<
      LaravelDefaultResponse<{ adjustment: LicenceAdjustmentResponse }>
    >(endpoints.backoffice.organizationLicencesAdjust(organizationId), {
      body: payload,
      skipError: true,
    });

    return this._apiService
      .run<LaravelDefaultResponse<{ adjustment: LicenceAdjustmentResponse }>>(request)
      .pipe(map((response) => response.data.adjustment));
  }

  resumeQuantity(
    organizationId: string,
    payload: LicenceResumePayload,
  ): Observable<LicenceResumeResponse> {
    const request = this._apiService.post<
      LaravelDefaultResponse<{ resume: LicenceResumeResponse }>
    >(endpoints.backoffice.organizationLicencesResume(organizationId), {
      body: payload,
      skipError: true,
    });

    return this._apiService
      .run<LaravelDefaultResponse<{ resume: LicenceResumeResponse }>>(request)
      .pipe(map((response) => response.data.resume));
  }

  cancelOne(organizationId: string, licenceId: string, reason: string): Observable<Licence | null> {
    const request = this._apiService.post<LaravelDefaultResponse<LicenceShowResponse>>(
      endpoints.backoffice.organizationLicenceCancel(organizationId, licenceId),
      { body: { reason }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<LicenceShowResponse>>(request)
      .pipe(map((response) => this._toLicence(response.data.licence)));
  }

  resumeOne(organizationId: string, licenceId: string, reason: string): Observable<Licence | null> {
    const request = this._apiService.post<LaravelDefaultResponse<LicenceShowResponse>>(
      endpoints.backoffice.organizationLicenceResume(organizationId, licenceId),
      { body: { reason }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<LicenceShowResponse>>(request)
      .pipe(map((response) => this._toLicence(response.data.licence)));
  }

  toLicences(response: LaravelDefaultResponse<LicencePortfolioResponse> | undefined): Licence[] {
    return (response?.data?.licences ?? []).map((row) => new Licence(row));
  }

  toComponents(
    response: LaravelDefaultResponse<LicencePortfolioResponse> | undefined,
  ): LicenceComponent[] {
    return (response?.data?.components ?? []).map((row) => new LicenceComponent(row));
  }

  toTotal(response: LaravelDefaultResponse<LicencePortfolioResponse> | undefined): number {
    return response?.data?.total ?? 0;
  }

  toSubscriptionSummary(
    response: LaravelDefaultResponse<LicencePortfolioResponse> | undefined,
  ): LicencePortfolioResponse['subscription'] {
    return response?.data?.subscription ?? null;
  }

  toHalfStateTotal(response: LaravelDefaultResponse<LicencePortfolioResponse> | undefined): number {
    return response?.data?.half_state_total ?? 0;
  }

  private _toLicence(row: LicenceResponse | null | undefined): Licence | null {
    return row ? new Licence(row) : null;
  }

  private _params(
    filters: LicenceFilters,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    const params: Record<string, string | number> = { page, pageSize };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== '') {
        params[key] = String(value);
      }
    }

    return params;
  }
}
